/** Lane assignment helpers. Settings and places are stored in the database. */

export const ENCODING_NAMES = "names";
export const ENCODING_LETTER_NUMBER = "letterNumber";

export const MOVEMENT_STAY = "stay";
export const MOVEMENT_RIGHT = "right";
export const MOVEMENT_LEFT = "left";

export const DOUBLES_TOGETHER = "together";
export const DOUBLES_SPLIT = "split";

export const DEFAULT_MOVEMENT_RULES = {
  kind: "stay",
  afterGames: 1,
  who: "entirePair",
  unit: "pair",
};

export const DEFAULT_TOURNAMENT_LANE_SETTINGS = {
  movement: MOVEMENT_STAY,
  movePairs: 1,
  movementRules: { ...DEFAULT_MOVEMENT_RULES },
  encoding: ENCODING_NAMES,
  doublesPlacement: DOUBLES_SPLIT,
};

export const movementRulesFromUi = ({ movement, movePairs } = {}) => {
  if (!movement || movement === MOVEMENT_STAY) {
    return { ...DEFAULT_MOVEMENT_RULES };
  }
  return {
    kind: "constant",
    afterGames: 1,
    who: "entirePair",
    unit: "pair",
    count: Math.max(1, Number(movePairs) || 1),
    direction: movement === MOVEMENT_LEFT ? "left" : "right",
  };
};

export const uiFromMovementRules = (rules) => {
  if (!rules || rules.kind === "stay") {
    return { movement: MOVEMENT_STAY, movePairs: 1 };
  }
  if (rules.kind === "constant") {
    return {
      movement:
        rules.direction === "left" ? MOVEMENT_LEFT : MOVEMENT_RIGHT,
      movePairs: Math.max(1, Number(rules.count) || 1),
    };
  }
  return { movement: MOVEMENT_STAY, movePairs: 1 };
};

/** Setup only edits stay / constant pair moves. Other recipes stay as stored. */
export const movementRulesCanMapToSetupUi = (rules) => {
  if (!rules?.kind || rules.kind === "stay") return true;
  return (
    rules.kind === "constant" &&
    (rules.who || "entirePair") === "entirePair" &&
    (rules.unit || "pair") === "pair"
  );
};

export const resolveTournamentMovementRules = (previous, fields) => {
  const previousRules = previous?.movementRules;
  if (
    previousRules?.kind &&
    !movementRulesCanMapToSetupUi(previousRules)
  ) {
    const derived = uiFromMovementRules(previousRules);
    const formUnchanged =
      (fields?.movement || MOVEMENT_STAY) === derived.movement &&
      Math.max(1, Number(fields?.movePairs) || 1) ===
        Math.max(1, Number(derived.movePairs) || 1);
    if (formUnchanged) return previousRules;
  }
  return movementRulesFromUi(fields);
};

export const DEFAULT_SQUAD_LANE_SETTINGS = {
  startLane: "",
  endLane: "",
  blackouts: [],
  movementRules: null,
};

export const emptyTournamentLaneSettings = () => ({
  ...DEFAULT_TOURNAMENT_LANE_SETTINGS,
  movementRules: { ...DEFAULT_MOVEMENT_RULES },
});

export const settingsFromTournamentRow = (row) => {
  const fallback = emptyTournamentLaneSettings();
  if (!row) return fallback;
  const movementRules = row.movementRules?.kind
    ? row.movementRules
    : fallback.movementRules;
  return {
    ...fallback,
    encoding: row.encoding || fallback.encoding,
    doublesPlacement: row.doublesPlacement || fallback.doublesPlacement,
    movementRules,
    ...uiFromMovementRules(movementRules),
  };
};

export const settingsFromSquadRow = (row) => ({
  startLane: row?.startLane ?? "",
  endLane: row?.endLane ?? "",
  blackouts: Array.isArray(row?.blackouts) ? row.blackouts : [],
  movementRules: row?.movementRules?.kind ? row.movementRules : null,
});

export const placementsFromTeams = (teams) => {
  const next = {};
  (teams || []).forEach((team) => {
    const pairStart = pairStartFromLane(team?.startingPair);
    const slot = Number(team?.slot);
    if (pairStart && slot >= 1) {
      const lane = Number(team?.startingLane);
      next[team.id] = {
        pairStart,
        slot,
        startingLane:
          lane === pairStart || lane === pairStart + 1 ? lane : null,
      };
    }
  });
  return next;
};

export const rangeFromPlacements = (placements) => {
  const starts = Object.values(placements || {})
    .map((placement) => pairStartFromLane(placement?.pairStart))
    .filter((start) => start != null);
  if (!starts.length) return { startLane: "", endLane: "" };
  return {
    startLane: Math.min(...starts),
    endLane: Math.max(...starts),
  };
};

export const emptySquadLaneState = () => ({
  startLane: "",
  endLane: "",
  blackouts: [],
  placements: {},
});

export const laneStateFromSquadAndTeams = (squad, teams) => {
  const placements = placementsFromTeams(teams);
  const range = settingsFromSquadRow(squad);
  const derived = rangeFromPlacements(placements);
  const startLane = pairStartFromLane(range.startLane) || derived.startLane;
  const endLane = pairStartFromLane(range.endLane) || derived.endLane;
  return {
    startLane: startLane === "" || startLane == null ? "" : startLane,
    endLane: endLane === "" || endLane == null ? "" : endLane,
    blackouts: range.blackouts,
    placements,
  };
};

export const laneStatesFromPayload = (payload) => {
  const squads = payload?.squads || [];
  const teams = payload?.teams || [];
  const teamsBySquad = {};
  teams.forEach((team) => {
    if (!team?.squadID) return;
    (teamsBySquad[team.squadID] ||= []).push(team);
  });
  const next = {};
  squads.forEach((squad) => {
    if (!squad?.id) return;
    next[squad.id] = laneStateFromSquadAndTeams(
      squad,
      teamsBySquad[squad.id] || []
    );
  });
  return next;
};

/** Split doubles occupy the pair. Everyone else picks a starting lane. */
export const assignsStartingLane = (settings, bowlerQuantity) => {
  const qty = Number(bowlerQuantity) || 1;
  if (qty === 2 && settings?.doublesPlacement === DOUBLES_SPLIT) {
    return false;
  }
  return true;
};

export const physicalLane = (lane) => {
  const n = Number(lane);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.floor(n);
};

export const pairStartFromLane = (lane) => {
  const n = Number(lane);
  if (!Number.isFinite(n) || n < 1) return null;
  const whole = Math.floor(n);
  return whole % 2 === 0 ? whole - 1 : whole;
};

export const formatPair = (pairStart) => {
  const start = pairStartFromLane(pairStart);
  if (start == null) return "";
  return `${start}-${start + 1}`;
};

export const parseBlackoutsText = (text) => {
  if (!text || !String(text).trim()) return [];
  const starts = String(text)
    .split(/[,;\n]+/)
    .map((part) => {
      const match = String(part).trim().match(/\d+/);
      return match ? pairStartFromLane(match[0]) : null;
    })
    .filter((n) => n != null);
  return [...new Set(starts)].sort((a, b) => a - b);
};

export const formatBlackoutsText = (blackouts) =>
  (blackouts || []).map((start) => formatPair(start)).join(", ");

export const emptySquadLaneForm = () => ({
  startLane: "",
  endLane: "",
  blackoutsText: "",
  placements: {},
});

export const squadLaneFormFromState = (state) => ({
  startLane:
    state?.startLane === "" || state?.startLane == null
      ? ""
      : String(state.startLane),
  endLane:
    state?.endLane === "" || state?.endLane == null
      ? ""
      : String(state.endLane),
  blackoutsText: formatBlackoutsText(state?.blackouts),
  placements: state?.placements || {},
});

export const slotCountForEvent = (settings, bowlerQuantity) => {
  const qty = Number(bowlerQuantity) || 1;
  if (qty >= 3) return 1;
  if (qty === 2) return 2;
  return 4;
};

export const slotCountForSquad = (teamCount, pairCount) => {
  const teams = Number(teamCount) || 0;
  const pairs = Number(pairCount) || 0;
  if (pairs < 1 || teams < 1) return 1;
  return Math.ceil(teams / pairs);
};

/** Letter or number printed on recaps when encoding is letterNumber. */
export const encodingsForSlot = (slot, settings, bowlerQuantity) => {
  const qty = Number(bowlerQuantity) || 1;
  if (settings?.encoding !== ENCODING_LETTER_NUMBER) return [];
  if (qty >= 3) return [];
  if (qty === 1) {
    const labels = ["A", "B", "C", "D"];
    return [labels[slot - 1] || String(slot)];
  }
  if (settings?.doublesPlacement === DOUBLES_TOGETHER) {
    return slot === 1 ? ["A", "B"] : ["1", "2"];
  }
  const left = ["A", "B", "C"][slot - 1];
  const right = ["1", "2", "3"][slot - 1];
  return left && right ? [left, right] : [];
};

export const encodingForBowlerIndex = (
  slot,
  bowlerIndex,
  settings,
  bowlerQuantity
) => {
  const codes = encodingsForSlot(slot, settings, bowlerQuantity);
  return codes[bowlerIndex] || "";
};

export const slotLabel = (slot, settings, bowlerQuantity) => {
  const qty = Number(bowlerQuantity) || 1;
  if (qty >= 3) return "On this pair";
  if (qty === 2 && settings?.doublesPlacement === DOUBLES_TOGETHER) {
    return slot === 1 ? "Left lane" : "Right lane";
  }
  if (qty === 2) {
    const left = ["A", "B", "C"][slot - 1];
    const right = ["1", "2", "3"][slot - 1];
    if (left && right) return `${left} / ${right}`;
  }
  if (qty === 1) {
    return ["A", "B", "C", "D"][slot - 1] || String(slot);
  }
  return String(slot);
};

export const pairPlaceFieldLabel = (settings, bowlerQuantity) => {
  const count = slotCountForEvent(settings, bowlerQuantity);
  const qty = Number(bowlerQuantity) || 1;
  if (qty >= 3) return "On this pair";
  if (qty === 2 && settings?.doublesPlacement === DOUBLES_TOGETHER) {
    return "Left or right lane";
  }
  if (qty === 2) {
    return count === 3 ? "A / 1, B / 2, or C / 3" : "A / 1 or B / 2";
  }
  return "A, B, C, or D";
};

export const movementSummary = (settings) => {
  const rules = settings?.movementRules?.kind
    ? settings.movementRules
    : movementRulesFromUi(settings);
  if (!rules || rules.kind === "stay") {
    return "Stay on the same pair";
  }
  if (rules.kind === "swapPairs") {
    return "Swap with the next pair";
  }
  if (rules.kind === "skipList") {
    return "Skip a different number of pairs each game";
  }
  const count = Number(rules.count) || Number(settings?.movePairs) || 1;
  const unit = rules.unit === "lane" ? (count === 1 ? "1 lane" : `${count} lanes`) : (count === 1 ? "1 pair" : `${count} pairs`);
  const who =
    rules.who === "byStartingLane"
      ? "Left lane left, right lane right"
      : rules.direction === "left"
        ? `Move ${unit} to the left`
        : `Move ${unit} to the right`;
  return who;
};

export const encodingSummary = (settings) => {
  if (settings?.encoding === ENCODING_LETTER_NUMBER) {
    return "A, B, C on the left lane, 1, 2, 3 on the right";
  }
  return "Names";
};

export const doublesPlacementSummary = (settings) => {
  if (settings?.doublesPlacement === DOUBLES_TOGETHER) {
    return "Whole team on one lane";
  }
  return "Split across the pair";
};

export const laneRulesBowlerLines = (settings, { hasDoubles } = {}) => {
  if (!settings) return [];
  const lines = [movementSummary(settings)];
  if (hasDoubles) lines.push(doublesPlacementSummary(settings));
  return lines.filter(Boolean);
};

export const buildPairs = (startLane, endLane, blackouts = []) => {
  const start = pairStartFromLane(startLane);
  const end = pairStartFromLane(endLane);
  if (start == null || end == null || end < start) return [];
  const blocked = new Set((blackouts || []).map((n) => pairStartFromLane(n)));
  const pairs = [];
  for (let lane = start; lane <= end; lane += 2) {
    pairs.push({
      pairStart: lane,
      label: formatPair(lane),
      blocked: blocked.has(lane),
    });
  }
  return pairs;
};

export const teamLabel = (team) => {
  if (team?.teamName) return team.teamName;
  const names = (team?.bowlers || [])
    .map((bowler) => bowler.name)
    .filter(Boolean);
  if (names.length) return names.join(" / ");
  return "Team";
};

const shuffleList = (items, random = Math.random) => {
  const next = [...(items || [])];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
};

export const autoAssignPlacements = ({
  teams,
  pairs,
  existingPlacements = {},
  shuffle = shuffleList,
  assignLanes = false,
}) => {
  const openPairs = (pairs || []).filter((pair) => !pair.blocked);
  const taken = new Set(
    Object.entries(existingPlacements)
      .filter(([, value]) => value?.pairStart && value?.slot)
      .map(([teamId]) => teamId)
  );
  const next = { ...existingPlacements };
  const allTeams = teams || [];
  const pairCount = openPairs.length;
  const base = pairCount ? Math.floor(allTeams.length / pairCount) : 0;
  const extra = pairCount ? allTeams.length % pairCount : 0;
  const seats = [];
  shuffle(openPairs).forEach((pair, index) => {
    const slotsOnPair = base + (index < extra ? 1 : 0);
    for (let slot = 1; slot <= slotsOnPair; slot += 1) {
      const occupied = Object.values(next).some(
        (placement) =>
          placement.pairStart === pair.pairStart && placement.slot === slot
      );
      if (!occupied) {
        seats.push({
          pairStart: pair.pairStart,
          slot,
          startingLane: assignLanes
            ? slot % 2 === 1
              ? pair.pairStart
              : pair.pairStart + 1
            : null,
        });
      }
    }
  });
  const queue = shuffle(allTeams.filter((team) => !taken.has(team.id)));
  const openSeats = shuffle(seats);
  const count = Math.min(queue.length, openSeats.length);
  for (let i = 0; i < count; i += 1) {
    next[queue[i].id] = openSeats[i];
  }
  return next;
};

export const teamBowlerIds = (team) =>
  (team?.bowlers || [])
    .map((bowler) => bowler.id || bowler.bowlerID)
    .filter(Boolean);

/** How many singles seats a doubles pair needs (one doubles team is two singles). */
export const slotCountNeededOnPair = ({
  sourceTeams = [],
  placements = {},
  pairStart,
}) => {
  const start = pairStartFromLane(pairStart);
  if (!start) return 0;
  return (sourceTeams || []).reduce((count, source) => {
    if (pairStartFromLane(placements?.[source.id]?.pairStart) !== start) {
      return count;
    }
    const fromRoster = (source.bowlers || []).length;
    const fromSize = Number(source.bowlerQuantity) || 0;
    return count + Math.max(fromRoster, fromSize, 1);
  }, 0);
};

export const slotCountForBoard = (teamCount, pairCount, fullestPairCount = 0) =>
  Math.max(
    slotCountForSquad(teamCount, pairCount),
    Math.max(0, Number(fullestPairCount) || 0)
  );

export const startingLaneForCopiedBowler = ({
  pairStart,
  startingLane,
  bowlerIndex,
  bowlerQuantity,
  doublesPlacement,
}) => {
  const start = pairStartFromLane(pairStart);
  if (start == null) return null;
  const qty = Number(bowlerQuantity) || 1;
  if (qty === 2 && (doublesPlacement || DOUBLES_SPLIT) === DOUBLES_SPLIT) {
    return bowlerIndex === 0 ? start : start + 1;
  }
  const assigned = physicalLane(startingLane);
  if (assigned === start || assigned === start + 1) return assigned;
  return start;
};

export const copyLanePlacements = ({
  sourceTeams = [],
  targetTeams = [],
  placements = {},
  doublesPlacement = DOUBLES_SPLIT,
  slotCount = 4,
  openPairStarts,
}) => {
  const next = { ...placements };
  const allowedPairs =
    openPairStarts == null
      ? null
      : new Set(
          (openPairStarts || [])
            .map((lane) => pairStartFromLane(lane))
            .filter(Boolean)
        );
  const targetsByBowler = new Map();
  (targetTeams || []).forEach((team) => {
    if (!team?.id || next[team.id]) return;
    teamBowlerIds(team).forEach((bowlerId) => {
      const list = targetsByBowler.get(bowlerId) || [];
      list.push(team);
      targetsByBowler.set(bowlerId, list);
    });
  });
  const targetIds = new Set((targetTeams || []).map((team) => team.id));
  const targetPlacements = () =>
    Object.fromEntries(
      Object.entries(next).filter(([teamId]) => targetIds.has(teamId))
    );
  const cap = Math.max(1, Number(slotCount) || 1);
  const sources = [...(sourceTeams || [])].sort((a, b) => {
    const placeA = next[a.id] || {};
    const placeB = next[b.id] || {};
    return (
      (placeA.pairStart || 0) - (placeB.pairStart || 0) ||
      (placeA.slot || 0) - (placeB.slot || 0)
    );
  });
  sources.forEach((source) => {
    const place = next[source.id];
    const pairStart = pairStartFromLane(place?.pairStart);
    if (!pairStart) return;
    if (allowedPairs && !allowedPairs.has(pairStart)) return;
    const bowlers = source.bowlers || [];
    const qty = Math.max(bowlers.length, Number(source.bowlerQuantity) || 1);
    const pairCap = Math.max(
      cap,
      slotCountNeededOnPair({
        sourceTeams: sources,
        placements: next,
        pairStart,
      })
    );
    bowlers.forEach((bowler, bowlerIndex) => {
      const bowlerId = bowler.id || bowler.bowlerID;
      const candidates = targetsByBowler.get(bowlerId) || [];
      const target = candidates.find((team) => !next[team.id]);
      if (!target) return;
      const slot = nextOpenSlot(targetPlacements(), pairStart, pairCap);
      if (!slot) return;
      next[target.id] = {
        pairStart,
        slot,
        startingLane: startingLaneForCopiedBowler({
          pairStart,
          startingLane: place.startingLane,
          bowlerIndex,
          bowlerQuantity: qty,
          doublesPlacement,
        }),
      };
    });
  });
  return next;
};

export const nextOpenSlot = (placements, pairStart, slotCount) => {
  const used = new Set(
    Object.values(placements || {})
      .filter((placement) => placement?.pairStart === pairStart)
      .map((placement) => placement.slot)
  );
  for (let slot = 1; slot <= slotCount; slot += 1) {
    if (!used.has(slot)) return slot;
  }
  return null;
};

/** Plan a starting-lane save on Edit Team. Blank input clears the assignment. */
export const planStartingLaneSave = ({
  laneInput,
  team,
  squadTeams = [],
  squadRow = null,
  settings,
  bowlerQuantity,
}) => {
  const pairStart = pairStartFromLane(laneInput);
  if (!pairStart) {
    return { startingPair: null, startingLane: null, slot: null };
  }

  const range = settingsFromSquadRow(squadRow || {});
  const openPairs = buildPairs(
    range.startLane,
    range.endLane,
    range.blackouts
  ).filter((pair) => !pair.blocked);
  const others = (squadTeams || []).filter((row) => {
    if (row.id === team?.id) return false;
    if (team?.eventsID && row.eventsID && row.eventsID !== team.eventsID) {
      return false;
    }
    return true;
  });
  const placements = placementsFromTeams(others);
  const sameEventCount = (squadTeams || []).filter((row) => {
    if (team?.eventsID && row.eventsID) return row.eventsID === team.eventsID;
    return true;
  }).length;
  const squadTeamCount = Math.max(
    sameEventCount,
    others.length + (team?.id ? 1 : 0)
  );
  const cap = openPairs.length
    ? slotCountForSquad(squadTeamCount, openPairs.length)
    : teamsOnPair(placements, pairStart).length + 1;
  const keepSlot =
    pairStartFromLane(team?.startingPair) === pairStart &&
    Number(team?.slot) >= 1
      ? Number(team.slot)
      : null;
  const slotValue = keepSlot || nextOpenSlot(placements, pairStart, cap);
  if (!slotValue) {
    return { error: `Pair ${formatPair(pairStart)} is full.` };
  }

  const laneValue = physicalLane(laneInput);
  return {
    startingPair: pairStart,
    startingLane: assignsStartingLane(settings, bowlerQuantity)
      ? laneValue
      : null,
    slot: slotValue,
  };
};

export const teamsOnPair = (placements, pairStart) =>
  Object.entries(placements || {})
    .filter(([, value]) => value?.pairStart === pairStart)
    .sort((a, b) => (a[1].slot || 0) - (b[1].slot || 0));

export const placementOccupant = (placements, pairStart, slot) => {
  const entry = Object.entries(placements || {}).find(
    ([, value]) => value?.pairStart === pairStart && value?.slot === slot
  );
  return entry ? entry[0] : null;
};

export const teamsOnLane = (placements, pairStart, startingLane) =>
  teamsOnPair(placements, pairStart).filter(
    ([, value]) => Number(value?.startingLane) === Number(startingLane)
  );

export const laneDisplayForTeam = (team) => {
  const pairStart = pairStartFromLane(team?.startingPair);
  if (!pairStart) {
    return { pairLabel: "", laneLabel: "", bowlers: team?.bowlers || [] };
  }
  const lane = Number(team?.startingLane);
  const laneLabel =
    lane === pairStart || lane === pairStart + 1 ? String(lane) : "";
  return {
    pairLabel: formatPair(pairStart),
    laneLabel,
    slot: team.slot || null,
    bowlers: team.bowlers || [],
  };
};

export const laneAssignmentLabel = (team) => {
  const display = laneDisplayForTeam(team);
  if (display.laneLabel) return `Lane ${display.laneLabel}`;
  if (display.pairLabel) return `Pair ${display.pairLabel}`;
  return "";
};
